import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface MFASetupProps {
  onComplete: () => void;
  onCancel: () => void;
}

interface MFAEnrollmentData {
  id: string;
  secret: string;
  qr_code: string;
  uri: string;
}

const MFASetup: React.FC<MFASetupProps> = ({ onComplete, onCancel }) => {
  
  const [step, setStep] = useState<'enroll' | 'verify'>('enroll');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [enrollmentData, setEnrollmentData] = useState<MFAEnrollmentData | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [secretKey, setSecretKey] = useState('');

  useEffect(() => {
    startEnrollment();
  }, []);

  const startEnrollment = async () => {
    try {
      setLoading(true);
      setError('');

      // Use Supabase's built-in MFA enrollment
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Admin Authenticator App' // Always provide a friendly name
      });

      if (error) {
        throw error;
      }

      setEnrollmentData({
        id: data.id,
        secret: data.totp.secret,
        qr_code: data.totp.qr_code,
        uri: data.totp.uri
      });
      setSecretKey(data.totp.secret);
      
    } catch (error: any) {
      setError(error.message || 'Failed to start MFA enrollment');
    } finally {
      setLoading(false);
    }
  };

  const verifyEnrollment = async () => {
    if (!verificationCode || !enrollmentData) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setLoading(true);
      setError('');

      
      // Use challengeAndVerify which handles challenge creation and verification in one step
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: enrollmentData.id,
        code: verificationCode
      });

      if (error) {
        throw new Error(error.message || 'Failed to verify MFA code');
      }

      // MFA setup complete
      
      // Show success message briefly before completing
      setError(''); // Clear any previous errors
      // Add a small delay to show success state
      setTimeout(() => {
        onComplete();
      }, 500);
    } catch (error: any) {
      setError(error.message || 'Failed to verify MFA code');
    } finally {
      setLoading(false);
    }
  };

  const copySecretKey = () => {
    navigator.clipboard.writeText(secretKey);
    // You could add a toast notification here
  };

  if (loading && !enrollmentData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Setting up MFA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg mb-6">
            <svg className="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-3">
            Enable Multi-Factor Authentication
          </h2>
          <p className="text-lg text-gray-600">
            {step === 'enroll' ? 'Set up your authenticator app' : 'Verify your setup'}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-soft border border-white/20 p-8">
          {step === 'enroll' ? (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Step 1: Add to Authenticator App
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </p>
                
                {enrollmentData?.qr_code ? (
                  <div className="flex justify-center mb-6">
                    <div className="bg-white p-4 rounded-lg shadow-sm border">
                      <img 
                        src={enrollmentData.qr_code} 
                        alt="MFA QR Code" 
                        className="w-48 h-48"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center mb-6">
                    <div className="bg-gray-100 p-4 rounded-lg border-2 border-dashed border-gray-300 w-48 h-48 flex items-center justify-center">
                      <p className="text-gray-500 text-sm">Loading QR code...</p>
                    </div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600 mb-2">Or enter this secret key manually:</p>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                      {secretKey || 'Loading secret key...'}
                    </code>
                    <button
                      onClick={copySecretKey}
                      className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm transition-colors"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Popular Authenticator Apps:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Google Authenticator</li>
                        <li>Authy</li>
                        <li>Microsoft Authenticator</li>
                        <li>1Password</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={onCancel}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setStep('verify')}
                  className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Next: Verify
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Step 2: Verify Setup
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Enter the 6-digit code from your authenticator app to complete the setup
                </p>
              </div>

              <div>
                <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <input
                  id="verificationCode"
                  type="text"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg text-center text-2xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="000000"
                  autoComplete="off"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={() => setStep('enroll')}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={verifyEnrollment}
                  disabled={loading || verificationCode.length !== 6}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </div>
                  ) : (
                    'Complete Setup'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500">
            MFA adds an extra layer of security to your admin account
          </p>
        </div>
      </div>
    </div>
  );
};

export default MFASetup;
